import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals } from '@/constants/theme';
import { supabase } from '@/lib/supabase';

declare const window: { location?: { hash: string } } | undefined;

export default function ResetPasswordScreen() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasCheckedUrl = useRef(false);
  const router = useRouter();

  useEffect(() => {
    if (hasCheckedUrl.current) return;
    hasCheckedUrl.current = true;

    const checkSessionAndUrl = async () => {
      if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.location) return;

      const hash = window.location.hash;
      if (!hash.includes('access_token') || !hash.includes('type=recovery')) return;

      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (!accessToken || !refreshToken || type !== 'recovery') return;

      if (!supabase) return;

      try {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (sessionError) setError('Failed to authenticate reset link. Please try again.');
      } catch {
        setError('Error processing reset link.');
      }
    };

    checkSessionAndUrl();
  }, []);

  const handleUpdatePassword = async () => {
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 8 || password.length > 64) {
      setError(
        password.length < 8
          ? 'Password must be at least 8 characters'
          : 'Password must be at most 64 characters'
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setLoading(true);

    try {
      if (!supabase) {
        setError('Supabase is not configured. Please set environment variables.');
        setLoading(false);
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        setError(updateError.message);
      } else {
        setPassword('');
        setConfirmPassword('');
        router.replace('/(tabs)');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    password.length >= 8 &&
    password.length <= 64 &&
    confirmPassword.length >= 8 &&
    confirmPassword.length <= 64 &&
    password === confirmPassword;

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.header}>
            <TouchableOpacity
              onPress={() => router.back()}
              style={styles.backButton}
              activeOpacity={0.7}
            >
              <Ionicons name="arrow-back" size={24} color={Brand.primary} />
            </TouchableOpacity>
            <View style={styles.iconContainer}>
              <Ionicons name="key" size={48} color={Brand.primary} />
            </View>
            <Text style={styles.title}>New Password</Text>
            <Text style={styles.subtitle}>Enter your new password below</Text>
          </View>

          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Ionicons name="lock-closed" size={20} color={Brand.primary} />
              <Text style={styles.cardTitle}>Update Password</Text>
            </View>

            {error ? (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle" size={16} color={Brand.destructive} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}

            <Input
              label="New Password"
              placeholder="8-64 characters"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password-new"
              value={password}
              onChangeText={setPassword}
              style={styles.input}
              editable={!loading}
            />

            <Input
              label="Confirm Password"
              placeholder="Confirm new password"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password-new"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              style={styles.input}
              editable={!loading}
            />

            <Button
              onPress={handleUpdatePassword}
              disabled={!isFormValid || loading}
              loading={loading}
              style={styles.submitButton}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>

            <View style={styles.footer}>
              <Text style={styles.footerText}>Remember your password?</Text>
              <Button
                variant="ghost"
                onPress={() => router.push('/(auth)/sign-in')}
                style={styles.linkButton}
              >
                Sign In
              </Button>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    justifyContent: 'center',
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    padding: 8,
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Brand.primary,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    textAlign: 'center',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 24,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Brand.destructiveLight,
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: Brand.destructive,
  },
  input: {
    marginBottom: 16,
  },
  submitButton: {
    marginTop: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
    gap: 8,
  },
  footerText: {
    fontSize: 14,
    color: Neutrals.textSecondary,
  },
  linkButton: {
    paddingHorizontal: 0,
  },
});
